import {
  Agent,
  CallQueryParams,
  CallRecord,
  CallSummaryResponse,
  CallTranscriptionResponse,
  ListCallsResponse,
  NumberAssociationRawResponse,
  NumberAssociationResponse,
  SignedUrlResponse,
} from "../types/calls.types";

import { apiFetch } from "./apiService";

const buildQuery = (params: CallQueryParams): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.append(key, String(value));
    }
  });
  return search.toString();
};

// Agents response envelope wasn't specified alongside the column and
// query-param spec - normalise defensively (bare array vs wrapped object)
// so the UI works whichever shape the live API actually returns.
type AgentsResponse =
  (Agent | string)[] | { agents?: (Agent | string)[]; items?: (Agent | string)[] };

const normaliseAgentsResponse = (data: unknown): Agent[] => {
  const body = data as AgentsResponse | null;
  const raw = Array.isArray(body) ? body : (body?.agents ?? body?.items ?? []);
  return raw.map((a) => (typeof a === "string" ? { name: a } : a));
};

export const CallsService = {
  listCalls: async (params: CallQueryParams): Promise<ListCallsResponse> => {
    const res = await apiFetch(`data-warehouse/calls?${buildQuery(params)}`, {
      method: "GET",
    });
    if (!res.ok) throw new Error(`Failed to load calls (${String(res.status)})`);
    return (await res.json()) as ListCallsResponse;
  },

  getCall: async (callId: string): Promise<CallRecord> => {
    const res = await apiFetch(`data-warehouse/calls/${callId}`, {
      method: "GET",
    });
    if (!res.ok) throw new Error(`Failed to load call (${String(res.status)})`);
    return (await res.json()) as CallRecord;
  },

  getSignedAudioUrl: async (callId: string): Promise<SignedUrlResponse> => {
    const res = await apiFetch(`data-warehouse/calls/${callId}/signed_url`, {
      method: "GET",
    });
    if (!res.ok) throw new Error(`Failed to get recording URL (${String(res.status)})`);
    return (await res.json()) as SignedUrlResponse;
  },

  // Resolves null when the call has no transcription record yet (404), so
  // the UI can treat that as "not available" rather than an error.
  getTranscription: async (callId: string): Promise<CallTranscriptionResponse | null> => {
    const res = await apiFetch(`data-warehouse/calls/${callId}/transcription`, {
      method: "GET",
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`Failed to load transcription (${String(res.status)})`);
    return (await res.json()) as CallTranscriptionResponse;
  },

  // Same as getTranscription - null (404) when the call hasn't been
  // summarised (yet).
  getSummary: async (callId: string): Promise<CallSummaryResponse | null> => {
    const res = await apiFetch(`data-warehouse/calls/${callId}/summary`, {
      method: "GET",
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`Failed to load summary (${String(res.status)})`);
    return (await res.json()) as CallSummaryResponse;
  },

  listAgents: async (excludeInactive = true): Promise<Agent[]> => {
    const search = new URLSearchParams({
      exclude_inactive: String(excludeInactive),
    });
    const res = await apiFetch(`data-warehouse/agents/list?${search}`, {
      method: "GET",
    });
    if (!res.ok) throw new Error(`Failed to load agents (${String(res.status)})`);
    return normaliseAgentsResponse(await res.json());
  },

  // Costs 3 Zoho API credits per call - only invoke on explicit user action.
  // Expects the raw (un-normalised) number; encoded since it may contain "+".
  getNumberAssociations: async (phoneNumber: string): Promise<NumberAssociationResponse> => {
    const res = await apiFetch(
      `data-remote/number-associated-with/${encodeURIComponent(phoneNumber)}`,
      { method: "GET" },
    );
    if (!res.ok) throw new Error(`Failed to look up number (${String(res.status)})`);
    const body = (await res.json()) as NumberAssociationRawResponse;
    const matches = [body.accounts, body.contacts, body.leads].flatMap(
      (group) => group?.matches ?? [],
    );
    return { count: matches.length, matches };
  },
};
